var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import config from "../config";
import { isUsageLimitBody, newIdempotencyKey, reportUsageLimit, UsageLimitError, } from "./usageLimits";
class ApiService {
    constructor() {
        this.baseURL = config.API_URL;
    }
    request(endpoint, options = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const url = `${this.baseURL}${endpoint}`;
            // Merge headers properly - don't let ...options overwrite the merged headers
            const { headers: optionHeaders } = options, restOptions = __rest(options, ["headers"]);
            const response = yield fetch(url, Object.assign(Object.assign({}, restOptions), { headers: Object.assign({ "Content-Type": "application/json" }, optionHeaders) }));
            if (!response.ok) {
                let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
                try {
                    const errorData = yield response.json();
                    if (isUsageLimitBody(errorData)) {
                        reportUsageLimit(errorData);
                        throw new UsageLimitError(errorData);
                    }
                    // Nest errors carry a readable `message` (validation errors: a list of them)
                    const { message } = errorData;
                    errorMessage =
                        typeof message === "string"
                            ? message
                            : Array.isArray(message)
                                ? message.join(", ")
                                : JSON.stringify(errorData);
                    console.error("API Error Details:", errorData);
                }
                catch (parseError) {
                    if (parseError instanceof UsageLimitError)
                        throw parseError;
                    // If parsing JSON fails, use the text response
                    const errorText = yield response.text();
                    if (errorText) {
                        errorMessage = errorText;
                    }
                }
                throw new Error(errorMessage);
            }
            return response.json();
        });
    }
    getCandidateRequestStatus(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/candidate-request/status", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getCandidatesForReview(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/candidate-request/review", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getUser(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/user/self", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getCandidatesByUser(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/candidate-request/by-user", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // Lists API methods - DEPRECATED: Use listService.ts instead (communicates via extension)
    // These methods are kept for reference but should not be used directly
    // All list operations should go through the extension service worker
    // For special endpoints that are not available through extension:
    getCandidatesByList(token, listId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/lists/${listId}/candidates`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    getCandidatesWithoutLists(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/lists/special/no-lists/candidates", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    getAllCandidatesByUser(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/lists/special/all/candidates", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    // Enrichment API methods (Legacy - used by UI)
    getEnrichmentCost(token, candidateIds, enrichmentType) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/ln-candidate/enrichment-cost", {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ candidateIds, enrichmentType }),
            });
        });
    }
    enrichCandidates(token, candidateIds, enrichmentType, aiPrompt) {
        return __awaiter(this, void 0, void 0, function* () {
            const payload = {
                candidateIds,
                enrichmentType,
            };
            if (aiPrompt) {
                payload.aiPrompt = aiPrompt;
            }
            const stringifiedBody = JSON.stringify(payload);
            return this.request("/ln-candidate/enrich-batch", {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                    // One key per click, so a retried request can't be charged twice
                    "Idempotency-Key": newIdempotencyKey(),
                },
                body: stringifiedBody,
            });
        });
    }
    /**
     * Stream enrichment progress using Server-Sent Events (SSE)
     * Provides real-time updates as candidates are enriched
     */
    enrichCandidatesStream(token, candidateIds, enrichmentType, aiPrompt, onProgress, onError, onComplete) {
        const params = new URLSearchParams({
            candidateIds: candidateIds.join(","),
            enrichmentType,
            token,
            idempotencyKey: newIdempotencyKey(),
        });
        if (aiPrompt) {
            params.append("aiPrompt", aiPrompt);
        }
        const url = `${this.baseURL}/ln-candidate/enrich-batch-stream?${params.toString()}`;
        const eventSource = new EventSource(url);
        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                if (data.event === "error" && isUsageLimitBody(data.limitReached)) {
                    reportUsageLimit(data.limitReached);
                }
                onProgress(data);
                if (data.event === "complete") {
                    eventSource.close();
                    onComplete();
                }
            }
            catch (error) {
                console.error("Failed to parse SSE message:", error);
                onError(error instanceof Error ? error : new Error("Failed to parse SSE message"));
            }
        };
        eventSource.onerror = (event) => {
            console.error("SSE error:", event);
            eventSource.close();
            onError(new Error("Server-Sent Events connection error"));
        };
        return () => {
            eventSource.close();
        };
    }
    // Customer-facing enrichment endpoints with batching
    enrichInBatches(token, endpoint, candidates, batchSize = 25) {
        return __awaiter(this, void 0, void 0, function* () {
            const batches = [];
            for (let i = 0; i < candidates.length; i += batchSize) {
                batches.push(candidates.slice(i, i + batchSize));
            }
            let totalProcessed = 0;
            let totalSkipped = 0;
            const allResults = [];
            for (const batch of batches) {
                const response = yield this.request(endpoint, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ candidates: batch }),
                });
                totalProcessed += response.processed;
                totalSkipped += response.skipped;
                if (response.results) {
                    allResults.push(...response.results);
                }
            }
            return {
                success: true,
                totalProcessed,
                totalSkipped,
                results: allResults,
            };
        });
    }
    /**
     * LinkedIn Enrichment - Customer endpoint
     */
    enrichLinkedIn(token, candidates) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.enrichInBatches(token, "/linkedin", candidates);
        });
    }
    /**
     * Ready For Change Enrichment - Customer endpoint
     */
    enrichReadyForChange(token, candidates) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.enrichInBatches(token, "/ready-for-change", candidates);
        });
    }
    /**
     * Email Enrichment - Customer endpoint
     */
    enrichEmail(token, candidates) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.enrichInBatches(token, "/email", candidates);
        });
    }
    /**
     * Personal Email Enrichment - Customer endpoint (ContactOut)
     */
    enrichPersonalEmail(token, candidates) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.enrichInBatches(token, "/personal-email", candidates);
        });
    }
    /**
     * Phone Enrichment - Customer endpoint
     */
    enrichPhone(token, candidates) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.enrichInBatches(token, "/phone", candidates);
        });
    }
    /**
     * Email Verification - Customer endpoint
     */
    verifyEmail(token, candidates) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.enrichInBatches(token, "/verify-email", candidates);
        });
    }
    // API Key Management
    getApiKeys(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/api-keys`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to get API keys: ${error}`);
            }
            return response.json();
        });
    }
    updateApiKeys(token, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/api-keys`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to update API keys: ${error}`);
            }
            return response.json();
        });
    }
    deleteApiKey(token, provider) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/api-keys/${provider}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to delete API key: ${error}`);
            }
            return response.json();
        });
    }
    testApiKey(token, provider, apiKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/api-keys/test`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ provider, apiKey }),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to test API key: ${error}`);
            }
            return response.json();
        });
    }
    // CSV Import (Legacy - parses CSV on frontend)
    importCsv(token, data) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/ln-candidate/import-csv`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to import CSV: ${error}`);
            }
            return response.json();
        });
    }
    // CSV Upload (New - uploads raw CSV file, server-side parsing)
    uploadCsv(token, formData) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/ln-candidate/upload-csv`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    // Don't set Content-Type - browser will set it with boundary for FormData
                },
                body: formData,
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to upload CSV: ${error}`);
            }
            return response.json();
        });
    }
    // CSV Upload with progress streaming (two-step process)
    uploadCsvInit(token, formData) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/ln-candidate/upload-csv-init`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: formData,
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to upload CSV: ${error}`);
            }
            return response.json();
        });
    }
    // Get base URL for SSE connections
    getBaseURL() {
        return this.baseURL;
    }
    // ========== MERGE UNIFIED INTEGRATION ==========
    /**
     * Test Merge API connection
     */
    testMergeConnection(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/test-connection", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Generate a Link Token for Merge Link UI
     */
    generateMergeLinkToken(token, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/link-token", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Exchange public token for account token after Merge Link completion
     */
    exchangeMergePublicToken(token, publicToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/exchange-token", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ publicToken }),
            });
        });
    }
    /**
     * List all linked accounts from Merge
     */
    listMergeLinkedAccounts(token, category) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = category ? `?category=${category}` : "";
            return this.request(`/merge/linked-accounts${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Get details of a linked account
     */
    getMergeLinkedAccount(token, accountToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/merge/linked-account?accountToken=${encodeURIComponent(accountToken)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Delete a linked account (disconnect integration)
     */
    deleteMergeLinkedAccount(token, accountToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/merge/linked-account?accountToken=${encodeURIComponent(accountToken)}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Get sync status for a linked account
     */
    getMergeSyncStatus(token, accountToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/merge/sync-status?accountToken=${encodeURIComponent(accountToken)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Trigger manual sync for a linked account
     */
    triggerMergeSync(token, accountToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/trigger-sync", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ accountToken }),
            });
        });
    }
    /**
     * Sync candidates to a connected ATS via Merge
     */
    syncCandidatesToAts(token, candidateIds, accountToken, platformName) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/ats/sync-candidates", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ candidateIds, accountToken, platformName }),
            });
        });
    }
    /**
     * Sync a single candidate to a connected ATS via Merge
     */
    syncCandidateToAts(token, candidateId, accountToken, platformName) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/ats/sync-candidate", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ candidateId, accountToken, platformName }),
            });
        });
    }
    /**
     * List jobs from a connected ATS via Merge
     */
    listAtsJobs(token, accountToken, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const params = new URLSearchParams({ accountToken });
            if (options === null || options === void 0 ? void 0 : options.cursor)
                params.append("cursor", options.cursor);
            if (options === null || options === void 0 ? void 0 : options.pageSize)
                params.append("pageSize", options.pageSize.toString());
            return this.request(`/merge/ats/jobs?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Sync candidate as a contact to a connected CRM via Merge
     */
    syncCandidateAsContact(token, candidateId, accountToken, platformName) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/crm/sync-as-contact", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ candidateId, accountToken, platformName }),
            });
        });
    }
    /**
     * Sync candidate as a lead to a connected CRM via Merge
     */
    syncCandidateAsLead(token, candidateId, accountToken, platformName) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/merge/crm/sync-as-lead", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ candidateId, accountToken, platformName }),
            });
        });
    }
    // ========== LOXO INTEGRATION (DEPRECATED - Use Merge instead) ==========
    /**
     * @deprecated Use Merge integration methods instead. Loxo is now supported through Merge Unified API.
     * Get Loxo API key settings
     */
    getLoxoSettings(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/loxo-settings`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to get Loxo settings: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Update Loxo API key
     */
    updateLoxoApiKey(token, loxoApiKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/loxo-api-key`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ loxoApiKey }),
            });
            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = yield response.json();
                    errorMessage = errorData.message || errorData.error || errorMessage;
                }
                catch (_a) {
                    try {
                        const errorText = yield response.text();
                        if (errorText) {
                            errorMessage = errorText;
                        }
                    }
                    catch (_b) {
                        // Use default error message
                    }
                }
                throw new Error(errorMessage);
            }
            return response.json();
        });
    }
    /**
     * Delete Loxo API key
     */
    deleteLoxoApiKey(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/loxo-api-key`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to delete Loxo API key: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Test Loxo API connection
     */
    testLoxoConnection(token, bearerToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/loxo/test-connection`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ bearerToken }),
            });
            if (!response.ok) {
                let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                try {
                    const errorData = yield response.json();
                    errorMessage = errorData.message || errorData.error || errorMessage;
                }
                catch (_a) {
                    try {
                        const errorText = yield response.text();
                        if (errorText) {
                            errorMessage = errorText;
                        }
                    }
                    catch (_b) {
                        // Use default error message
                    }
                }
                throw new Error(`Failed to test Loxo connection: ${errorMessage}`);
            }
            return response.json();
        });
    }
    // MillionVerifier API Key Management
    /**
     * Get MillionVerifier settings
     */
    getMillionVerifierSettings(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/millionverifier-settings`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to get MillionVerifier settings: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Update MillionVerifier API key
     */
    updateMillionVerifierApiKey(token, millionVerifierApiKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/millionverifier-api-key`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ millionVerifierApiKey }),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to update MillionVerifier API key: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Delete MillionVerifier API key
     */
    deleteMillionVerifierApiKey(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/millionverifier-api-key`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to delete MillionVerifier API key: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Test MillionVerifier API connection
     */
    testMillionVerifierConnection(token, millionVerifierApiKey) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/user/millionverifier-api-key/test`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ millionVerifierApiKey }),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to test MillionVerifier connection: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Sync single candidate to Loxo
     */
    syncCandidateToLoxo(token, candidateId, bearerToken, listId) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/loxo/sync-candidate`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ candidateId, bearerToken, listId }),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to sync candidate to Loxo: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Sync multiple candidates to Loxo
     */
    syncCandidatesToLoxo(token, candidateIds, bearerToken, listId) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/loxo/sync-candidates-bulk`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ candidateIds, bearerToken, listId }),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to sync candidates to Loxo: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Get available Loxo lists
     */
    getLoxoLists(token) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/loxo/lists`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to fetch Loxo lists: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Import person from Loxo to BringOnBoard
     */
    importPersonFromLoxo(token, loxoPersonId, bearerToken) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/loxo/import-person`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ loxoPersonId, bearerToken }),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to import person from Loxo: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Import all people from Loxo
     */
    importAllPeopleFromLoxo(token, bearerToken, limit = 100) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/loxo/import-all-people`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ bearerToken, limit }),
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to import all people from Loxo: ${error}`);
            }
            return response.json();
        });
    }
    /**
     * Get all people from Loxo (for preview/selection)
     */
    getAllPeopleFromLoxo(token, bearerToken, limit = 100, offset = 0) {
        return __awaiter(this, void 0, void 0, function* () {
            const response = yield fetch(`${this.baseURL}/loxo/get-all-people?bearerToken=${encodeURIComponent(bearerToken)}&limit=${limit}&offset=${offset}`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) {
                const error = yield response.text();
                throw new Error(`Failed to get people from Loxo: ${error}`);
            }
            return response.json();
        });
    }
    // ============================================
    // ORGANIZATIONS API
    // ============================================
    /**
     * Get current user's organization
     */
    getMyOrganization(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/organizations/my-organization", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    /**
     * Get organization by ID
     */
    getOrganization(token, orgId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/organizations/${orgId}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    /**
     * Create a new organization
     */
    createOrganization(token, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/organizations", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Update organization
     */
    updateOrganization(token, orgId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/organizations/${orgId}`, {
                method: "PUT",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Add member to organization
     */
    addOrganizationMember(token, orgId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/organizations/${orgId}/members`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Remove member from organization
     */
    removeOrganizationMember(token, orgId, memberId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/organizations/${orgId}/members/${memberId}`, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    /**
     * Get organization members
     */
    getOrganizationMembers(token, orgId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/organizations/${orgId}/members`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    /**
     * Get organization credit status
     */
    getOrganizationCredits(token, orgId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/organizations/${orgId}/credits`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
        });
    }
    createLinkedInSearch(token, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/linkedin-search", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(data),
            });
        });
    }
    getLinkedInSearches(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/linkedin-search", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getLinkedInSearch(token, id) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/linkedin-search/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    createLinkedInSearchStreamTicket(token, searchId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/linkedin-search/${searchId}/stream/ticket`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // --- Search Session API ---
    createSearchSession(token, target) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/search-session", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(target ? { target } : {}),
            });
        });
    }
    createSearchSessionStreamTicket(token, sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}/stream/ticket`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    applySessionFilters(token, sessionId, filters, target) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}/apply-filters`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(Object.assign({ filters }, (target ? { target } : {}))),
            });
        });
    }
    fetchAutocomplete(token, sessionId, filterType, query) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}/fetch-autocomplete`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ filterType, query }),
            });
        });
    }
    startSessionSearch(token, sessionId, page) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}/search`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ page: page !== null && page !== void 0 ? page : 1 }),
            });
        });
    }
    enrichSessionProfiles(token, sessionId, linkedInIds) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}/enrich`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ linkedInIds }),
            });
        });
    }
    closeSearchSession(token, sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    startSessionExport(token, sessionId, listId, limit) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}/export`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ listId, limit }),
            });
        });
    }
    cancelSessionExport(token, sessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/search-session/${sessionId}/cancel-export`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getTargetMarkets(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/target-markets", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Create a new target market
     */
    createTargetMarket(token, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/target-markets", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Get a target market by ID with segments tree
     */
    getTargetMarket(token, id) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Update a target market
     */
    updateTargetMarket(token, id, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${id}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Delete a target market
     */
    deleteTargetMarket(token, id) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Create a segment in a target market
     */
    createSegment(token, targetMarketId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${targetMarketId}/segments`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Update a segment
     */
    updateSegment(token, targetMarketId, segmentId, data) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${targetMarketId}/segments/${segmentId}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(data),
            });
        });
    }
    /**
     * Delete a segment
     */
    deleteSegment(token, targetMarketId, segmentId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${targetMarketId}/segments/${segmentId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    /**
     * Scrape a target market
     */
    scrapeTargetMarket(token, id, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${id}/scrape`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(options || {}),
            });
        });
    }
    /**
     * Scrape a segment
     */
    scrapeSegment(token, targetMarketId, segmentId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/target-markets/${targetMarketId}/segments/${segmentId}/scrape`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(options || {}),
            });
        });
    }
    // Deals API
    getDeals(token, accountToken) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals?accountToken=${encodeURIComponent(accountToken)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getConversation(token, email) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/conversation/${encodeURIComponent(email)}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    generateFollowup(token, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/generate-followup", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(request),
            });
        });
    }
    generateReplyFromThread(token, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/generate-reply-from-thread", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(request),
            });
        });
    }
    // Replies from watched leads caught in our Gmail mailboxes (Instantly sometimes misses them)
    getOpenLeadReplies(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/lead-replies", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    getOpenLeadReplyCount(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/lead-replies/count", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    scanLeadRepliesNow(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/lead-replies/scan", { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        });
    }
    getLeadReplyThread(token, replyId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/lead-replies/${encodeURIComponent(replyId)}/thread`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    markLeadReplyDone(token, replyId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.request(`/v1/lead-replies/${encodeURIComponent(replyId)}/done`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    snoozeLeadReply(token, replyId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/lead-replies/${encodeURIComponent(replyId)}/snooze`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    sendLeadReply(token, replyId, body) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/lead-replies/${encodeURIComponent(replyId)}/send`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ body }),
            });
        });
    }
    // HubSpot Deals API - cached locally, use refresh=true to fetch from HubSpot
    getHotDeals(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/hot-deals", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    setStripeLinkSent(token, hubspotDealId, isSent) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/hot/stripe-link`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ isSent }),
            });
        });
    }
    /** Mark paid by hand, or undo it */
    setDealPaid(token, hubspotDealId, isPaid) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/hot/paid`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ isPaid }),
            });
        });
    }
    setHotPinned(token, hubspotDealId, isPinned) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/hot/pin`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ isPinned }),
            });
        });
    }
    getMobileLookup(token, hubspotDealId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/mobile-lookup`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    saveWhatsAppDraft(token, hubspotDealId, draft) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/whatsapp/draft`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(draft),
            });
        });
    }
    setHasWhatsApp(token, hubspotDealId, hasWhatsApp) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/whatsapp/availability`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ hasWhatsApp }),
            });
        });
    }
    /** Spends Apollo credits */
    startMobileLookup(token, hubspotDealId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/mobile-lookup`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getAllDeals(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/all", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    getHubSpotDeals(token, refresh = false) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/hubspot${refresh ? "?refresh=true" : ""}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // Get cache info for HubSpot deals
    getHubSpotDealsCacheInfo(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/hubspot/cache-info", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // Instantly Leads API - Simplified version without HubSpot
    getPositiveLeads(token, limit = 50) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/positive-leads?limit=${limit}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // Send reply via Instantly, or via Gmail when Instantly has no email from the lead to reply to
    markFollowupDone(token, hubspotDealId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/deals/${encodeURIComponent(hubspotDealId)}/mark-followup-done`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    sendReply(token, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/send-reply", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(request),
            });
        });
    }
    // Follow-up review: saving or skipping never sends; only approveFollowupDraft does
    getFollowupReviewList(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/followup-drafts", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    saveFollowupDraft(token, hubspotDealId, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/followup-drafts/deals/${encodeURIComponent(hubspotDealId)}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(request),
            });
        });
    }
    skipFollowup(token, hubspotDealId, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/followup-drafts/deals/${encodeURIComponent(hubspotDealId)}/skip`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(request),
            });
        });
    }
    updateFollowupDraft(token, draftId, request) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/followup-drafts/${encodeURIComponent(draftId)}`, {
                method: "PATCH",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(request),
            });
        });
    }
    discardFollowupDraft(token, draftId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/followup-drafts/${encodeURIComponent(draftId)}/discard`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    approveFollowupDraft(token, draftId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/followup-drafts/${encodeURIComponent(draftId)}/approve-send`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // Public: the registration page shows the purchase before the buyer has an account
    getCheckoutSession(checkoutSessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/billing/checkout-sessions/${encodeURIComponent(checkoutSessionId)}`);
        });
    }
    claimCheckoutSession(token, checkoutSessionId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/billing/checkout-sessions/${encodeURIComponent(checkoutSessionId)}/claim`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // --- Plan limits and usage ---
    getUsage(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/usage", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    createTopUpCheckout(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/billing/topup-checkout", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // --- Admin: plans, settings and accounts (staff only) ---
    getAdminPlans(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/admin/usage/plans", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    savePlan(token, planKey, changes) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/admin/usage/plans/${encodeURIComponent(planKey)}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(changes),
            });
        });
    }
    getBillingSettings(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/admin/usage/settings", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    saveBillingSettings(token, changes) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/admin/usage/settings", {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(changes),
            });
        });
    }
    getUsageAccounts(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/admin/usage/accounts", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    getUsageAccount(token, organizationId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/admin/usage/accounts/${organizationId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    assignAccountPlan(token, organizationId, planKey) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/admin/usage/accounts/${organizationId}/plan`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ planKey }),
            });
        });
    }
    /** null clears an override so the plan's value applies again */
    setAccountLimitOverrides(token, organizationId, overrides) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/admin/usage/accounts/${organizationId}/overrides`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(overrides),
            });
        });
    }
    adjustAccountCredits(token, organizationId, adjustment) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request(`/v1/admin/usage/accounts/${organizationId}/credits`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify(adjustment),
            });
        });
    }
    getGlobalUsage(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/admin/usage/global", { headers: { Authorization: `Bearer ${token}` } });
        });
    }
    // Lookup a single HubSpot contact by ID
    lookupHubSpotContact(token, contactId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/hubspot/contact", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: JSON.stringify({ contactId }),
            });
        });
    }
    // Get today's follow-up queue (deals due for follow-up today)
    getTodaysFollowups(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/todays-followups", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    // Get follow-up scheduling stats
    getFollowupStats(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/followup-stats", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
    getGmailStatus(token) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.request("/v1/deals/gmail-status", {
                headers: { Authorization: `Bearer ${token}` },
            });
        });
    }
}
export default new ApiService();
